import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Edit, Eye, Globe, MoreVertical, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useNavigate } from 'react-router-dom';

/* eslint-disable-next-line import/no-extraneous-dependencies */
import { useHeroToast } from '../../../../hooks';
import {
  useDeleteAttractionFromFirestore,
} from '../../../../queries';
import { ResponseAttractionDataProps } from '../../../../queries/attractionQueryFirebase/useGetAttractionCollectionFirebase';
import { TranslationStatusRow } from '../../../../utils';

const ActionsCell: React.FC<{ attraction: ResponseAttractionDataProps }> = ({ attraction }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useHeroToast();
  const { mutate: deleteAttraction, isPending } = useDeleteAttractionFromFirestore();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleDelete = () => {
    deleteAttraction(
      { attractionId: attraction.id },
      {
        onSuccess: () => {
          showSuccess('Successfully deleted attraction');
          queryClient.invalidateQueries({ queryKey: ['attractions'] });
          setShowDeleteConfirm(false);
        },
        onError: () => {
          showError('Error while deleting attraction');
          setShowDeleteConfirm(false);
        },
      },
    );
  };

  const isBusy = isPending;

  return (
    <div className="flex items-center justify-end h-[40px]">
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            isDisabled={isBusy}
            aria-label="Open actions menu"
          >
            <MoreVertical size={16} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label="Attraction actions"
          onAction={(key) => {
            switch (key) {
              case 'view':
                navigate(`/attractions/${attraction.id}`);
                break;
              case 'edit':
                navigate(`/attractions/${attraction.id}/edit`);
                break;
              case 'delete':
                setShowDeleteConfirm(true);
                break;
              default:
                break;
            }
          }}
        >
          {[
            <DropdownItem key="view" startContent={<Eye size={14} />}>
              View
            </DropdownItem>,
            <DropdownItem key="edit" startContent={<Edit size={14} />}>
              Edit
            </DropdownItem>,
            <DropdownItem
              key="delete"
              color="danger"
              className="text-danger"
              startContent={<Trash2 size={14} />}
            >
              Delete
            </DropdownItem>,
          ]}
        </DropdownMenu>
      </Dropdown>

      <Modal
        isOpen={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        size="sm"
        isDismissable={!isPending}
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className="flex flex-col gap-1">Delete attraction?</ModalHeader>
              <ModalBody>
                <p className="text-sm text-gray-600">This action cannot be undone.</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose} isDisabled={isPending}>
                  Cancel
                </Button>
                <Button color="danger" onPress={handleDelete} isLoading={isPending}>
                  Delete
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export const AttractionColumns: ColumnDef<ResponseAttractionDataProps>[] = [
  {
    accessorKey: 'title',
    header: 'Title',
    cell: ({ row }) => row.original.title?.pl ?? '',
  },
  {
    accessorKey: 'city',
    header: 'City',
  },
  {
    accessorKey: 'languages',
    header: 'Languages',
    enableSorting: false,
    cell: ({ row }) => {
      const languages = row.original.languages ?? [];

      if (!languages.length) {
        return <span className="text-gray-400">0</span>;
      }

      return (
        <Popover placement="bottom">
          <PopoverTrigger>
            <button
              type="button"
              className="flex items-center gap-2 cursor-pointer hover:opacity-70 transition-opacity"
            >
              <Globe size={16} />
              <span className="text-sm">{languages.length}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="px-2 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                {languages.map((language) => (
                  <ReactCountryFlag
                    key={language}
                    countryCode={language === 'en' ? 'gb' : language}
                    svg
                    style={{ width: 24, height: 16 }}
                  />
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      );
    },
  },
  {
    accessorKey: 'type',
    header: 'Type',
  },
  {
    accessorKey: 'translationStatus',
    header: 'Translation status',
    cell: ({ row }) => <TranslationStatusRow value={row.original.translationStatus} />,
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) => <ActionsCell attraction={row.original} />,
  },
];
